'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { type ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { Chip } from '@module/mdrt-org/shared/components/chip/chip';
import { FieldController } from '@module/mdrt-org/shared/components/field-controller/field-controller';
import { Loader } from '@module/mdrt-org/shared/components/loader/loader';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { MAX_USERNAME_LENGTH } from '@module/mdrt-org/shared/utils/constants/inputs-constants';
import { patchAccountSettingsAction } from '@module/mdrt-org/shared/utils/data/patch-account-settings-action';
import { AccountSettingsFormSection } from '@module/mdrt-org/shared/utils/enums/account-settings-form-section';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { loginHandler } from '@module/mdrt-org/shared/utils/helpers/login-handler';
import { useAdB2cObjectId } from '@module/mdrt-org/shared/utils/hooks/use-ad-b2c-object-id';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { USERNAME_REGEXP } from '@module/mdrt-org/shared/utils/types/regexp';
import { AuthProviderName } from '@shared/misc/next-auth/enum';
import { parseJwtToken } from '@shared/misc/utils/parse-jwt-token';
import { CheckboxInput } from '@shared/ui-components/checkbox-input/checkbox-input';
import { ChipTheme } from '@shared/ui-components/chip/chipTheme';
import { FormInput } from '@shared/ui-components/form-input/form-input';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { CHANGE_USERNAME_TO_BE_UNIQUE_ERROR_ID } from '@shared/utils/constants/errors';
import { GTM_ACTION_CLICK, GTM_EVENT_ACCOUNT_PROFILE_UPDATE, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { useAccountSettingsPageContext } from '../../providers/account-settings-page-context';
import { type LoginPasswordDTO, type LoginPasswordFormData } from '../../utils/types/form-data';
import { type AccountSettingsLoginPasswordContent } from './get-account-settings-login-password-content';
import { SocialLogins } from './social-logins/social-logins';
import styles from './login-password-form.module.scss';
type LoginPasswordFormProps = {
  cmsContent: AccountSettingsLoginPasswordContent;
  tabUrl: string;
};
const filterUsernameByRegex = {
  input: (value: string) => {
    return value;
  },
  output: (event: ChangeEvent<HTMLInputElement>) => {
    return event.target.value.match(USERNAME_REGEXP)?.join('') || '';
  }
};
export const LoginPasswordForm = t0 => {
  const $ = _c(74);
  const {
    cmsContent,
    tabUrl
  } = t0;
  const locale = useLocale();
  const {
    customerProfileContextData,
    resetCustomerProfileContextData,
    setCustomerProfileContextData
  } = useAccountSettingsPageContext();
  const {
    accessToken,
    personId
  } = useUserContext();
  const userId = useAdB2cObjectId();
  const addErrorToast = useAddDefaultErrorToast();
  let t1;
  if ($[0] !== accessToken) {
    t1 = accessToken ? parseJwtToken(accessToken) : undefined;
    $[0] = accessToken;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsedToken = t1;
  const isBiometricSwitchEnabled = parsedToken !== undefined && parsedToken?.credentialId !== undefined;
  let t2;
  if ($[2] !== customerProfileContextData.emailAddress || $[3] !== customerProfileContextData.mdrtId || $[4] !== customerProfileContextData.username) {
    t2 = {
      defaultValues: {
        emailAddress: customerProfileContextData.emailAddress,
        mdrtId: customerProfileContextData.mdrtId,
        username: customerProfileContextData.username
      },
      mode: "onSubmit"
    };
    $[2] = customerProfileContextData.emailAddress;
    $[3] = customerProfileContextData.mdrtId;
    $[4] = customerProfileContextData.username;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const formMethods = useForm(t2);
  let t3;
  if ($[6] !== addErrorToast || $[7] !== cmsContent.usernameNotUniqueError || $[8] !== formMethods || $[9] !== resetCustomerProfileContextData) {
    t3 = result => {
      resetCustomerProfileContextData();
      if (result && "messageTranslationKey" in result && result.messageTranslationKey === CHANGE_USERNAME_TO_BE_UNIQUE_ERROR_ID) {
        formMethods.setError("username", {
          message: result.messageTranslationKey === CHANGE_USERNAME_TO_BE_UNIQUE_ERROR_ID ? cmsContent.usernameNotUniqueError : result.message,
          type: CHANGE_USERNAME_TO_BE_UNIQUE_ERROR_ID
        });
      } else {
        if (result.status !== 200 && "message" in result) {
          addErrorToast();
        }
      }
    };
    $[6] = addErrorToast;
    $[7] = cmsContent.usernameNotUniqueError;
    $[8] = formMethods;
    $[9] = resetCustomerProfileContextData;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const onFailure = t3;
  const {
    formState: t4,
    reset
  } = formMethods;
  const {
    isDirty
  } = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS];
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== formMethods || $[13] !== reset) {
    t6 = () => reset({
      ...formMethods.getValues()
    });
    $[12] = formMethods;
    $[13] = reset;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onFailure || $[16] !== t6) {
    t7 = {
      mutationFunction: patchAccountSettingsAction,
      mutationKey: t5,
      onFailure,
      onSuccessHandler: t6
    };
    $[15] = onFailure;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t7);
  let t8;
  if ($[18] !== customerProfileContextData || $[19] !== mutate || $[20] !== personId || $[21] !== setCustomerProfileContextData) {
    t8 = async data => {
      setCustomerProfileContextData({
        ...customerProfileContextData,
        username: data.username
      });
      mutate({
        formData: {
          accountSettingStep: AccountSettingsFormStep.LoginPassword,
          loginData: {
            username: data.username
          }
        },
        personId
      });
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_ACCOUNT_PROFILE_UPDATE,
        label: "Login Password Change"
      });
    };
    $[18] = customerProfileContextData;
    $[19] = mutate;
    $[20] = personId;
    $[21] = setCustomerProfileContextData;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleSubmit = t8;
  useConfirmNavigation(isDirty, tabUrl);
  let t9;
  if ($[23] !== cmsContent.tabTitle) {
    t9 = <h1 className={styles["login-password-form__heading"]}>{cmsContent.tabTitle}</h1>;
    $[23] = cmsContent.tabTitle;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FormInput disabled={true} />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== cmsContent.email || $[27] !== cmsContent.emailEditInformation) {
    t11 = <FieldController hint={cmsContent.emailEditInformation} label={cmsContent.email} name="emailAddress">{t10}</FieldController>;
    $[26] = cmsContent.email;
    $[27] = cmsContent.emailEditInformation;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <FormInput disabled={true} />;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== cmsContent.mdrtId) {
    t13 = <FieldController label={cmsContent.mdrtId} name="mdrtId">{t12}</FieldController>;
    $[30] = cmsContent.mdrtId;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== cmsContent.requiredLabel) {
    t14 = {
      required: cmsContent.requiredLabel
    };
    $[32] = cmsContent.requiredLabel;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <FormInput maxLength={MAX_USERNAME_LENGTH} />;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== cmsContent.userName || $[36] !== t14) {
    t16 = <FieldController label={cmsContent.userName} name="username" rules={t14} transform={filterUsernameByRegex}>{t15}</FieldController>;
    $[35] = cmsContent.userName;
    $[36] = t14;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== cmsContent.pagePaths.accountSettingPagePath || $[39] !== locale || $[40] !== userId) {
    t17 = () => loginHandler({
      authorizationParameters: {
        ui_locales: locale,
        userId
      },
      options: {
        redirectTo: cmsContent.pagePaths.accountSettingPagePath
      },
      provider: AuthProviderName.PasswordReset
    });
    $[38] = cmsContent.pagePaths.accountSettingPagePath;
    $[39] = locale;
    $[40] = userId;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== cmsContent.resetPassword || $[43] !== t17) {
    t18 = <Chip className={styles["login-password-form__reset-password-button"]} label={cmsContent.resetPassword} onClick={t17} theme={ChipTheme.Light} />;
    $[42] = cmsContent.resetPassword;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const t19 = !isDirty || isMutationPending;
  let t20;
  if ($[45] !== cmsContent.saveChanges || $[46] !== t19) {
    t20 = <div className={styles["login-password-form__footer"]}><Button disabled={t19} type={ButtonType.SUBMIT}>{cmsContent.saveChanges}</Button></div>;
    $[45] = cmsContent.saveChanges;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== isMutationPending) {
    t21 = isMutationPending && <Loader isInner={true} />;
    $[48] = isMutationPending;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== formMethods || $[51] !== handleSubmit || $[52] !== t11 || $[53] !== t13 || $[54] !== t16 || $[55] !== t18 || $[56] !== t20 || $[57] !== t21 || $[58] !== t9) {
    t22 = <FormWrapper className={styles["login-password-form"]} dataTestId="personal-info-form" formMethods={formMethods} onSubmit={handleSubmit}>{t9}{t11}{t13}{t16}{t18}{t20}{t21}</FormWrapper>;
    $[50] = formMethods;
    $[51] = handleSubmit;
    $[52] = t11;
    $[53] = t13;
    $[54] = t16;
    $[55] = t18;
    $[56] = t20;
    $[57] = t21;
    $[58] = t9;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== cmsContent.pagePaths.accountSettingPagePath || $[61] !== isBiometricSwitchEnabled || $[62] !== locale) {
    t23 = () => loginHandler({
      authorizationParameters: {
        ui_locales: locale
      },
      options: {
        redirectTo: `${cmsContent.pagePaths.accountSettingPagePath}#${AccountSettingsFormSection.LoginPassword}`
      },
      provider: isBiometricSwitchEnabled ? AuthProviderName.BiometricDeregister : AuthProviderName.BiometricRegistration
    });
    $[60] = cmsContent.pagePaths.accountSettingPagePath;
    $[61] = isBiometricSwitchEnabled;
    $[62] = locale;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== cmsContent.biometricsSwitchLabel || $[65] !== isBiometricSwitchEnabled || $[66] !== t23) {
    t24 = <div className={styles["biometric-switch"]}><CheckboxInput id="biometric-switch" isChecked={isBiometricSwitchEnabled} isSwitch={true} label={cmsContent.biometricsSwitchLabel} name="biometric-switch" onChange={t23} /></div>;
    $[64] = cmsContent.biometricsSwitchLabel;
    $[65] = isBiometricSwitchEnabled;
    $[66] = t23;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== cmsContent) {
    t25 = <SocialLogins cmsContent={cmsContent} />;
    $[68] = cmsContent;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== t22 || $[71] !== t24 || $[72] !== t25) {
    t26 = <>{t22}{t24}{t25}</>;
    $[70] = t22;
    $[71] = t24;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  return t26;
};